import React from "react";
import {
  filtersSelector,
} from "@app-redux/selectors";
import type {
  CategoryItem,
} from "@/types/category";
import { updateFilterCategoriesArr } from "@app-redux/slices/filtersSlice";
import { useAppDispatch, useAppSelector } from "@app-redux/store";
import { useCategories, useUpdateFavorites, useUserData, useUpdateCategoryItem, useDeleteCategoryItem } from "@/hooks/queries";
import type { Category } from "@/types/category";
import type { UserFavorites } from "@/types/user";
import { CountryFlag } from "@components/CountryFlag";
import { EditCategoryDialog } from "@components/EditCategoryDialog";
import { CategorySettingsSkeleton } from "@components/Skeletons";
import { FAVORITE_MAP } from "@constants/index";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import {
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
} from "@mui/material";
import { useNotification } from "@/hooks/useNotification";
import styles from "./styles.module.css";

const Icon = ({
  icon,
  onClick,
  label,
  disabled,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  label: string;
  disabled?: boolean;
}) => (
  <IconButton aria-label={label} onClick={onClick} disabled={disabled} sx={{ p: "0" }}>
    {icon}
  </IconButton>
);

const Item = ({
  id,
  name,
  count,
  isLocation,
  category,
}: CategoryItem & { isLocation: boolean; category: string }) => {
  const isUsed = Boolean(count);
  const dispatch = useAppDispatch();
  const { data: userData } = useUserData();
  const favorites: UserFavorites = userData?.preferences || {};
  const filters = useAppSelector(filtersSelector);
  const [editMode, setEditMode] = React.useState(false);
  const [value, setValue] = React.useState(name);
  const isFavorite = Object.values(favorites).flat().includes(id);
  const isAdmin = userData?.isAdmin;
  const updateCategoryItem = useUpdateCategoryItem();
  const deleteCategoryItem = useDeleteCategoryItem();
  const updateFavorites = useUpdateFavorites();
  const { notifySuccess, notifyError } = useNotification();
  const starIcon = isFavorite ? (
    <StarOutlinedIcon />
  ) : (
    <StarBorderOutlinedIcon />
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const save = async () => {
    try {
      await updateCategoryItem.mutateAsync({ id, value });
      notifySuccess("Category updated");
    } catch {
      setValue(name);
      notifyError("Failed to update category");
    }
    setEditMode(false);
  };

  const onKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      save();
    } else if (e.key === "Escape") {
      setValue(name);
      setEditMode(false);
    }
  };

  const onDelete = async () => {
    try {
      await deleteCategoryItem.mutateAsync(id);
      notifySuccess("Category deleted");
      const isFilterApplied = filters?.categories_values?.includes(id);
      if (isFilterApplied) {
        dispatch(
          updateFilterCategoriesArr({
            id,
            checked: true,
          })
        );
      }
    } catch {
      notifyError("Failed to delete category");
    }
  };

  const getFavoriteObj = () => {
    const obj: Record<string, string[]> = {};
    Object.values(FAVORITE_MAP).forEach((value) => {
      if (favorites[value]) {
        obj[value] = favorites[value]!;
      } else {
        obj[value] = [];
      }
    });
    return obj;
  };

  const onFavorite = async () => {
    const obj = getFavoriteObj();
    const favKey = FAVORITE_MAP[category as keyof typeof FAVORITE_MAP];
    obj[favKey] = isFavorite
      ? obj[favKey].filter((i: string) => i !== id)
      : [...obj[favKey], id];

    updateFavorites.mutate(obj as UserFavorites);
  };

  const flag = isLocation ? <CountryFlag country={name} /> : null;

  return !editMode ? (
    <ListItem
      disableGutters
      secondaryAction={
        <Stack direction="row" gap="8px">
          {!isUsed && isAdmin && (
            <Icon
              label="delete"
              onClick={onDelete}
              disabled={deleteCategoryItem.isPending}
              icon={<DeleteOutlineOutlinedIcon />}
            />
          )}
          {!isLocation && isAdmin && (
            <Icon
              label="edit"
              onClick={() => {
                setEditMode(!editMode);
              }}
              disabled={updateCategoryItem.isPending}
              icon={<EditOutlinedIcon />}
            />
          )}
          <Icon label="favorite" onClick={onFavorite} icon={starIcon} />
        </Stack>
      }
    >
      <ListItemText
        className={styles.profileCatTextContent}
        primary={
          <>
            {flag}
            {name}
          </>
        }
        secondary={`(${count})`}
      />
    </ListItem>
  ) : (
    <li>
      <TextField
        onBlur={() => save()}
        autoFocus
        fullWidth
        value={value || ""}
        onChange={onChange}
        onKeyDown={onKeyDown}
        type="text"
        placeholder="Category name"
        aria-label="Category name"
        variant="outlined"
        size="small"
      />
    </li>
  );
};

const CategoryList = ({
  name,
  items = [],
  isLocation,
}: Category & { isLocation: boolean }) => {
  const sortedItems = [...items].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <List
      className={styles.profileCatList}
      subheader={<ListSubheader disableGutters>{name}</ListSubheader>}
    >
      {sortedItems.map((data) => (
        <Item key={data.id} {...data} category={name} isLocation={isLocation} />
      ))}
    </List>
  );
};

export const ProfileCategoriesSection = ({ categoryName }: { categoryName: string }) => {
  const { data } = useCategories();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const { data: userData } = useUserData();
  const isAdmin = userData?.isAdmin;

  const category = data.find((c) => c.name === categoryName);

  if (!data.length) {
    return (
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{categoryName}</h2>
        <CategorySettingsSkeleton />
      </div>
    );
  }

  if (!category) return null;

  const isLocation = category.name === "Country";

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>{categoryName}</h2>
      <CategoryList {...category} isLocation={isLocation} />
      {isAdmin && (
        <Button
          className={styles.profileCatAdd}
          aria-label="add"
          onClick={() => setDialogOpen(true)}
        >
          <AddIcon fontSize="small" />
          Add New {category.name}
        </Button>
      )}
      {dialogOpen && (
        <EditCategoryDialog
          isLocation={isLocation}
          id={category.id}
          name={category.name}
          open={dialogOpen}
          close={() => setDialogOpen(false)}
        />
      )}
    </div>
  );
};
